import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchServices } from '../lib/supabase'

// ── LCA Quick Service Pills ───────────────────────────────────
// Smart shortcut card on the Dashboard. Shows the client's top 5
// most-used services in the last 90 days as clickable pills.
//
// Click → navigates to /client/new with the service pre-selected
// via URL param. NO borrower data is ever carried over — every
// click starts a fresh order.
//
// Auto-learning behavior:
//   0 orders         → card hidden
//   1-3 orders       → show what they've used + "more unlock soon"
//   4+ orders        → show top 5 by frequency (last 90 days)

const SHORTCUT_KEY = 'lca_quick_service_select' // localStorage handoff to NewOrder

export function getQuickServicePick() {
  // Helper for NewOrder to read & clear the pending pick
  try {
    const raw = localStorage.getItem(SHORTCUT_KEY)
    if (!raw) return null
    localStorage.removeItem(SHORTCUT_KEY)
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export default function QuickServicePills({ jobs }) {
  const navigate = useNavigate()
  const [services, setServices] = useState([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    fetchServices()
      .then(data => { setServices(data || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  // ── Compute top 5 services from last 90 days ─────────────────
  const topServices = React.useMemo(() => {
    if (loading || !services.length || !jobs) return []

    const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000)

    // Count service usage from jobs in last 90 days (excluding cancelled)
    const counts = {}
    for (const j of jobs) {
      if (!j.created_at || !j.signing_type) continue
      if (j.status === 'cancelled') continue
      const t = new Date(j.created_at).getTime()
      if (t < ninetyDaysAgo) continue
      counts[j.signing_type] = (counts[j.signing_type] || 0) + 1
    }

    // Build pill data — match service names to the catalog for prices/sectors
    const ranked = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => {
        const svc = services.find(s => s.name === name)
        return svc ? { ...svc, _count: count } : null
      })
      .filter(Boolean)

    return ranked
  }, [jobs, services, loading])

  // ── Determine display state ──────────────────────────────────
  const totalOrders = jobs ? jobs.filter(j => j.status !== 'cancelled').length : 0

  // Hide entirely for brand-new clients (0 orders)
  if (totalOrders === 0) return null
  // Don't render while services are still loading
  if (loading) return null
  // No matching services found (edge case — services renamed in DB?)
  if (topServices.length === 0) return null

  const isEarlyClient = totalOrders < 4

  function handlePillClick(svc) {
    // Stash the service pick for NewOrder to read on mount
    try {
      localStorage.setItem(SHORTCUT_KEY, JSON.stringify({
        signing_type: svc.name,
        client_price: svc.client_price,
        sector_hint:  svc.sector || null,
        picked_at:    new Date().toISOString(),
      }))
    } catch {
      // localStorage unavailable — proceed anyway, NewOrder will just start blank
    }
    navigate('/client/new')
  }

  return (
    <div style={{
      background:    '#fff',
      border:        '1px solid #E8E6DF',
      borderRadius:  16,
      padding:       '14px 16px',
      display:       'flex',
      flexDirection: 'column',
      justifyContent:'space-between',
      gap:           8,
      minHeight:     90,
    }}>
      {/* Header row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <div style={{
          fontSize:      9,
          color:         '#B8B5AB',
          letterSpacing: '0.12em',
          fontWeight:    600,
        }}>QUICK START</div>
        <div style={{
          fontSize:      9,
          color:         '#9A7A30',
          fontWeight:    600,
          letterSpacing: '0.04em',
        }}>{isEarlyClient ? 'YOUR SERVICES' : 'TOP 5 · LAST 90 DAYS'}</div>
      </div>

      {/* Pills */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
        {topServices.map(svc => (
          <button
            key={svc.id || svc.name}
            type="button"
            onClick={() => handlePillClick(svc)}
            title={`Start a new ${svc.name} order — fresh borrower info`}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:            6,
              padding:        '6px 12px',
              background:     '#FAFAF8',
              border:         '1px solid #E0D9CC',
              borderRadius:   20,
              cursor:         'pointer',
              fontFamily:     'inherit',
              fontSize:       11,
              color:          '#0D1B3E',
              fontWeight:     500,
              transition:     'all 0.15s',
              whiteSpace:     'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background    = '#FFFBF0'
              e.currentTarget.style.borderColor   = '#C9A84C'
              e.currentTarget.style.transform     = 'translateY(-1px)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background    = '#FAFAF8'
              e.currentTarget.style.borderColor   = '#E0D9CC'
              e.currentTarget.style.transform     = 'translateY(0)'
            }}
          >
            <span>{svc.name}</span>
            <span style={{ color:'#9A7A30', fontWeight:700 }}>${svc.client_price}</span>
          </button>
        ))}

        {isEarlyClient && (
          <span style={{
            fontSize:      10,
            color:         '#B8B5AB',
            fontStyle:     'italic',
            marginLeft:    4,
          }}>More shortcuts unlock as you order more.</span>
        )}
      </div>
    </div>
  )
}
